import { ref, computed, onMounted } from 'vue'
import { logger } from '@/utils/logger'

/**
 * Composable for detecting and managing embed/iframe mode
 * Use this to conditionally show/hide UI elements when app is embedded in Dayforce
 */

// Singleton state - shared across all component instances
const isInIframe = ref(false)
const detectedParentOrigin = ref(null)
const detectedParentHostname = ref(null)
const isInitialized = ref(false)

const TRUSTED_PARENT_DOMAINS = ['dayforcehcm.com', 'dayforce.com']
export const LOCAL_TESTING_ORIGINS = [
  'http://localhost',
  'file://',
  'https://staging-agentnoon-v2--' // allow preview web.app embeds for testing
]

export function getEmbedParentInfo(referrer) {
  let parentOrigin = 'unknown'
  let parentHostname = 'unknown'
  const embedReferrer = referrer ?? (typeof document !== 'undefined' ? document.referrer : '')

  try {
    if (embedReferrer) {
      const referrerUrl = new URL(embedReferrer)
      parentOrigin = referrerUrl.origin
      parentHostname = referrerUrl.hostname
    }
  } catch (error) {
    logger.warn('Could not determine parent origin', error.message)
  }

  return { parentOrigin, parentHostname }
}

export function isTrustedEmbedParent({ parentOrigin, parentHostname }) {
  const isTrustedDayforce = TRUSTED_PARENT_DOMAINS.some(
    (domain) => parentHostname === domain || parentHostname?.endsWith(`.${domain}`)
  )

  const isLocalTesting = LOCAL_TESTING_ORIGINS.some((local) => parentOrigin?.startsWith(local))
  return isTrustedDayforce || isLocalTesting
}

export function isTrustedEmbedOrigin(origin) {
  if (!origin) return false

  // file:// iframes send "null" origin
  if (origin === 'null') {
    return isTrustedEmbedParent({ parentOrigin: 'file://', parentHostname: null })
  }

  const { parentOrigin, parentHostname } = getEmbedParentInfo(origin)
  return isTrustedEmbedParent({ parentOrigin, parentHostname })
}

/**
 * Detect if running in iframe and identify parent origin
 */
function detectEmbedMode() {
  if (isInitialized.value) return

  if (typeof window === 'undefined') return

  // Check if in iframe
  isInIframe.value = window.self !== window.top

  if (isInIframe.value) {
    const { parentOrigin, parentHostname } = getEmbedParentInfo()
    detectedParentOrigin.value = parentOrigin
    detectedParentHostname.value = parentHostname
  }

  isInitialized.value = true

  // Log for debugging in development only to avoid noise/leaking origins
  if (process.env.NODE_ENV === 'development') {
    logger.info('[EMBED MODE]', {
      isInIframe: isInIframe.value,
      parentOrigin: detectedParentOrigin.value,
      parentHostname: detectedParentHostname.value
    })
  }
}

/**
 * Retrieve embed status without needing component lifecycle hooks.
 * Initializes detection on first use.
 */
export function getEmbedStatus() {
  if (!isInitialized.value) {
    detectEmbedMode()
  }

  const status = {
    isEmbedded: isInIframe.value,
    parentOrigin: detectedParentOrigin.value,
    parentHostname: detectedParentHostname.value
  }

  const isTrustedEmbed = isTrustedEmbedParent({
    parentOrigin: status.parentOrigin,
    parentHostname: status.parentHostname
  })

  return { ...status, isTrustedEmbed }
}

/**
 * Evaluate embed trust status from detected parent origin only.
 * Route-specific destination checks should be implemented by the caller.
 *
 * @returns {{ isEmbedded: boolean, isTrusted: boolean }}
 */
export function getEmbedTrustStatus() {
  const { isEmbedded, isTrustedEmbed } = getEmbedStatus()

  if (!isEmbedded) {
    return { isEmbedded: false, isTrusted: false }
  }

  return { isEmbedded: true, isTrusted: isTrustedEmbed }
}

export function useEmbedMode() {
  // Initialize on first use
  onMounted(() => {
    detectEmbedMode()
  })

  /**
   * Is the app currently running in an iframe?
   */
  const isEmbedded = computed(() => isInIframe.value)

  /**
   * Is the parent origin a trusted Dayforce platform?
   */
  const isTrustedEmbed = computed(() => {
    if (!isInIframe.value) return false

    return isTrustedEmbedParent({
      parentOrigin: detectedParentOrigin.value,
      parentHostname: detectedParentHostname.value
    })
  })

  return {
    isEmbedded,
    isTrustedEmbed
  }
}
